import { Divider, Icon } from '@design-system';
import { cn } from '@utils/cn';
import { useTranslation } from 'next-i18next';

import { Image } from '../../../Image';
import { ProductPrice } from '../../../ProductPrice/ProductPrice';
import { CurrencyCode } from '../types';

export type FramingSummaryState = 'framed' | 'unframed' | 'pending';

export interface FramingSummaryProduct {
  id: string;
  title: string;
  thumbnailSrc: string;
  thumbnailAlt: string;
}

export interface FramingSummaryCardProps {
  /** Product this card represents; drives the thumbnail + title header (omit for no header). */
  product?: FramingSummaryProduct;
  state: FramingSummaryState;
  /** Apply the error (red) treatment — used to flag a required-but-missing choice. */
  hasError?: boolean;
  /** Frame style name, e.g. "Black frame" (shown when `state === 'framed'`). */
  frameName?: string;
  /** Glazing name, e.g. "UV protective acrylic" (shown when `state === 'framed'`). */
  glazingName?: string;
  /** Mounting name, e.g. "Window mount" (shown when `state === 'framed'`). */
  mountingName?: string;
  /** Frame price to display as `+ price` (omitted or 0 → hidden). */
  price?: number;
  currencyCode?: CurrencyCode;
  /** Framed-by-default work: show "Included" instead of a price. */
  included?: boolean;
  onEdit: () => void;
}

/**
 * The filled framing summary shown after a choice is made. Purely presentational:
 * the parent decides which `state` to render.
 */
export const FramingSummaryCard = ({
  currencyCode,
  frameName,
  glazingName,
  hasError = false,
  included = false,
  mountingName,
  onEdit,
  price,
  product,
  state,
}: FramingSummaryCardProps) => {
  const { t } = useTranslation('checkout');
  const isPending = state === 'pending';
  const optionsLine = [glazingName, mountingName].filter(Boolean).join(' · ');

  return (
    <div
      className={cn(
        'w-full rounded-sm border',
        hasError ? 'border-danger-4' : 'border-neutral-2',
      )}
    >
      {product ? (
        <>
          <div className="flex items-center gap-3 p-4">
            <span className="h-10 w-10 shrink-0 overflow-hidden rounded-sm">
              <Image
                alt={product.thumbnailAlt}
                className="h-full w-full object-cover"
                src={product.thumbnailSrc}
                width={96}
              />
            </span>
            <span className="text-neutral-6">{product.title}</span>
          </div>
          <div className="px-4">
            <Divider />
          </div>
        </>
      ) : null}

      <button
        className="flex w-full items-start justify-between gap-3 p-4 text-left transition-colors hover:text-neutral-4"
        onClick={onEdit}
        type="button"
      >
        <span className="flex min-w-0 flex-col">
          {state === 'framed' ? (
            <>
              <span className="text-current">{frameName}</span>
              {optionsLine ? (
                <span className="line-clamp-1 text-neutral-4">
                  {optionsLine}
                </span>
              ) : null}
            </>
          ) : null}

          {state === 'unframed' ? (
            <span className="text-current">{t('framing.unframed')}</span>
          ) : null}

          {isPending ? (
            <span className={hasError ? 'text-danger-4' : 'text-current'}>
              {t('framing.selectOption')}
            </span>
          ) : null}
        </span>

        <span className="flex shrink-0 gap-2">
          {state === 'framed' && included ? (
            <span className="text-neutral-4">{t('framing.included')}</span>
          ) : null}

          {state === 'framed' && !included && !!price ? (
            <span>
              &#43;{' '}
              <ProductPrice
                currencyCode={currencyCode}
                price={price}
                skipConvertCurrency={!!currencyCode}
                skipTaxCalculation
              />
            </span>
          ) : null}

          <Icon name="ic_chevron_right" />
        </span>
      </button>
    </div>
  );
};
